import type { SemiLAM2 } from "./types";

import {
  collectionListItemToGoogleProduct,
  collectionListItemToGoogleSku,
  getCollectionNameForEvent,
  getPriceFromSku,
  getSingleProductAndSku,
  verifySingleProductAndSku,
} from "./utils";

// returns items, ingests transaction products
function purchaseListItemToGoogle(transaction) {
  let items = [];
  const products = transaction.products;
  for (let productId in products) {
    const product = products[productId];
    const skus = product.skus;
    const name = product.unifiedId;

    for (let skuId in skus) {
      const sku = skus[skuId];
      items.push({
        item_id: skuId,
        item_name: name,
        price: getPriceFromSku(sku),
        quantity: sku.quantity,
        item_sku: skuId,
        item_product_id: product,
      });
    }
  }
  return items;
}

function addRemoveCart(event: string, data: SemiLAM2) {
  const collectionOfProducts = data.payload.collections.products;
  const { productId, skuId } = verifySingleProductAndSku(
    collectionOfProducts,
    "added to cart"
  );
  const moneyValue = getPriceFromSku(
    collectionOfProducts[productId].skus[skuId]
  );
  const items = collectionListItemToGoogleSku(collectionOfProducts);
  return {
    event: event,
    data: {
      currency: data.context.app.currency,
      value: moneyValue,
      items,
    },
  };
}

function addRemoveWishlist(event: string, data: SemiLAM2) {
  const collectionName = getCollectionNameForEvent(event);
  const collection = data.payload.collections[collectionName];
  if (collection === undefined) {
    throw new Error("Collection [" + collectionName + "] is missing");
  }

  const { productId, skuId } = getSingleProductAndSku(collection);
  const moneyValue = getPriceFromSku(collection[productId].skus[skuId]);

  const items = collectionListItemToGoogleSku(collection);
  return {
    event,
    data: {
      currency: data.context.app.currency,
      value: moneyValue,
      items,
    },
  };
}

function pageView(event: string, _data: SemiLAM2) {
  return {
    event,
    data: {
      page_location: _data.context.document.location.href,
      page_referrer: _data.context.document.referrer,
    },
  };
}

function pageViewWithProducts(event: string, data: SemiLAM2) {
  const collectionName = getCollectionNameForEvent(event);
  let collection = data.payload.collections[collectionName];
  if (collection === undefined) {
    throw new Error("Collection [" + collectionName + "] is missing");
  }
  if (collection.products) {
    collection = collection.products;
  }
  // check if page type requires per sku or per product
  let items = collectionListItemToGoogleSku(collection);
  const hrefCheck = data.context.document.location.href;
  if (hrefCheck.includes("/c/") || hrefCheck.includes("/search")) {
    items = collectionListItemToGoogleProduct(collection);
  }
  const result = {
    event,
    data: {
      fusion_query_id: data.context.app.fusionVariant || "",
      items,
      value: undefined,
    },
  };

  if (event === "view_item") {
    const { productId, skuId } = verifySingleProductAndSku(
      collection,
      "view item"
    );
    const sku = collection[productId].skus[skuId];
    const price = getPriceFromSku(sku);
    result.data.value = price;
  } else if (event === "view_cart") {
    result.data.value = data.payload.collections["cart"]["total"]["subTotal"];
  } else if (event === "view_item_list" && data?.context?.app?.listType) {
    // Don't want to define a full type for the return here so this is workaround
    result.data["item_list_id"] = data.context.app.listType;
    result.data["item_list_name"] = data.context.app.listType;
  }

  return result;
}

function search(event: string, data: SemiLAM2) {
  return {
    event,
    data: {
      search_term: data.payload.event.data.search.term,
      search_type: data.payload.event.data.search.type,
      fusion_query_id: data.context.app.fusionVariant || "",
    },
  };
}

function useFilters(event: string, data: SemiLAM2) {
  const {
    type = "",
    value = "",
    interactionType = "",
    activeCount = 0,
  } = data?.payload?.event?.data?.filter ?? {};
  const fusionVariant = data.context.app.fusionVariant || "";

  // GA4 event params must be primitives; nesting them under `filter` serializes to "[object Object]".
  return {
    event,
    data: {
      filter_type: type,
      filter_value: value,
      filter_interaction_type: interactionType,
      filter_active_count: activeCount,
      fusion_query_id: fusionVariant,
    },
  };
}

function storeSelection(event: string, data: SemiLAM2) {
  const storeName =
    data?.payload?.event?.data?.storeName || "store value missing";
  return {
    event,
    data: {
      store_name: storeName,
    },
  };
}

function updateOptInStatus(event: string, data: SemiLAM2) {
  const subscribeMethod = data?.payload?.event?.data?.method || "unknown";
  return {
    event,
    data: {
      method: subscribeMethod,
    },
  };
}

function selectPromotion(event: string, data: SemiLAM2) {
  return {
    event,
    data: {
      promotion_id: data.payload.event.data.promotionId,
    },
  };
}

function checkoutEvent(event: string, data: SemiLAM2) {
  const cartCollection = data.payload.collections["cart"].products;
  const paymentType = data.payload.collections["cart"]["paymentType"] ?? ""; // Optional field
  const checkoutType = data.payload.collections["cart"]["checkoutType"] ?? ""; // Optional field

  if (cartCollection === undefined) {
    throw new Error("Cart collection is missing");
  }

  return {
    event,
    data: {
      coupon: data?.payload?.user?.session?.attribution?.coupon,
      currency: data.context.app.currency,
      payment_type: paymentType,
      checkout_type: checkoutType,
      value: data.payload.collections["cart"]["total"]["subTotal"],
      items: collectionListItemToGoogleSku(cartCollection),
    },
  };
}

function purchase(event: string, data: SemiLAM2) {
  const transaction = data.payload.collections["transaction"];

  if (transaction === undefined) {
    throw new Error("Transaction collection missing");
  }
  const items = collectionListItemToGoogleSku(transaction.products);
  return {
    event,
    data: {
      currency: data.context.app.currency,
      transaction_id: transaction.id,
      value: transaction.total.subTotal,
      coupon: transaction.promoCode, // saving for later data.payload.user?.session?.attribution?.coupon
      shipping: transaction.total.shipping,
      tax: transaction.total.tax,
      payment_type: (transaction.payment ?? "").replace(/\|/g, ":"), // Replaces "|" with ":" to align with Apps implementation
      items,
    },
  };
}

function clickEvent(event: string, data: SemiLAM2) {
  const component = data.payload.event.data["component"];

  if (component === undefined) {
    throw new Error("Component is missing");
  }
  // select_component
  const {
    id,
    class: className,
    aria: ariaLabel,
    type,
    value,
    text,
    url,
    name: componentName,
    details,
    location,
  } = component;
  const payload: any = {
    event,
    data: {
      component_id: id,
      component_class: className,
      component_aria_label: ariaLabel,
      component_type: type,
      component_value: value,
      component_text: text,
      component_url: url,
      component_name: componentName,
      component_details: details,
      component_location: location,
    },
  };
  if (event === "select_link") {
    const { id, url, text } = component;
    payload.data = {
      ...payload.data,
      select_id: id,
      select_uri: url,
      select_text: text,
    };
  }
  return payload;
}

function productInteraction(event: string, productInteraction: SemiLAM2) {
  const { id, name, category, skuId, price, salePrice } =
    productInteraction.payload.event.data;
  const discount = salePrice > 0 ? Math.max(0, price - salePrice) : undefined;

  const item = {
    item_id: id,
    item_name: name,
    item_category: category,
    item_variant: (skuId || "").length > 0 ? skuId : "not available",
    price: salePrice > 0 ? salePrice : Math.max(0, price),
    item_price_type: salePrice > 0 ? "markdown" : "regular",
    discount,
  };

  return {
    event,
    data: {
      items: [item],
      fusion_query_id: productInteraction.context.app.fusionVariant || "",
    },
  };
}

function auth(event: string, data: SemiLAM2) {
  const method = data?.payload?.event?.data?.method || "unknown";

  return {
    event,
    data: {
      method,
    },
  };
}

function emailSignup(event: string, data: SemiLAM2) {
  const method = data?.payload?.event?.data?.method || "unknown";
  return {
    event,
    data: {
      method,
    },
  };
}

function addCoupon(event: string, data: SemiLAM2) {
  return {
    event,
    data: {
      coupon: data.payload.event.data.coupon,
    },
  };
}

function error(event: string, data: SemiLAM2) {
  return {
    event,
    data: {
      error_message: data.payload.event.attributes.errorMessage,
      error_details: data.payload.event.attributes.errorDetails,
      error_type: data.payload.event.attributes.errorType,
      error_guest_facing: data.payload.event.attributes.errorGuestFacing,
    },
  };
}
// mapping lam actionTypes to handlers and setting GA4 event names
/* add more events like so...
  lam_actionType: {
    name: "ga4_event_name",
    handler: functionName,
  },
*/
export default {
  "checkout-add-payment": {
    name: "add_payment_info",
    handler: checkoutEvent,
  },
  "checkout-add-shipping": {
    name: "add_shipping_info",
    handler: checkoutEvent,
  },
  "add-to-cart": {
    name: "add_to_cart",
    handler: addRemoveCart,
  },
  "add-to-wishlist": {
    name: "add_to_wishlist",
    handler: addRemoveWishlist,
  },
  "remove-from-wishlist": {
    name: "remove_from_wishlist",
    handler: addRemoveWishlist,
  },
  "checkout-start": {
    name: "begin_checkout",
    handler: checkoutEvent,
  },
  "checkout-review-order": {
    name: "select_review_order",
    handler: checkoutEvent,
  },
  purchase: {
    name: "purchase",
    handler: purchase,
  },
  "remove-from-cart": {
    name: "remove_from_cart",
    handler: addRemoveCart,
  },
  "cart-view": {
    name: "view_cart",
    handler: pageViewWithProducts,
  },
  "product-view": {
    name: "view_item",
    handler: pageViewWithProducts,
  },
  "page-view": {
    name: "page_view",
    handler: pageView,
  },
  "component-click": {
    name: "select_component",
    handler: clickEvent,
  },
  "add-coupon": {
    name: "add_coupon",
    handler: addCoupon,
  },
  // "component-impression": {
  //   name: "component_impression",
  //   handler: clickEvent,
  // },
  // "component-link-click": {
  //   name: "select_link",
  //   handler: clickEvent,
  // },
  "product-interaction": {
    name: "select_item",
    handler: productInteraction,
  },
  "product-list-view": {
    name: "view_item_list",
    handler: pageViewWithProducts,
  },
  // "register-success": {
  //   name: "sign_up",
  //   handler: auth,
  // },
  "search-executed": {
    name: "search",
    handler: search,
  },
  "filter-applied": {
    name: "use_filters",
    handler: useFilters,
  },
  "select-promotion": {
    name: "select_promotion",
    handler: selectPromotion,
  },
  "login-success": {
    name: "login",
    handler: auth,
  },
  "store-selected": {
    name: "store_selected",
    handler: storeSelection,
  },
  unsubscribe: {
    name: "unsubscribe",
    handler: updateOptInStatus,
  },
  "email-signup": {
    name: "email_signup",
    handler: emailSignup,
  },
  "error-api": {
    name: "view_error",
    handler: error,
  },
  // "logout-success": {
  //   name: "logout",
  //   handler: auth,
  // },
};
